/*======================================================================================================================
  * Bluebell: src/app/state/results-filters/results-filters.effects.ts
  * Last Edited: 13/12/2024
  * Copyright 2024-2025 Bramble CFD LTD
  * The contents of this file are NOT for redistribution
  * See AUTHORS for list of developers on this project
 =====================================================================================================================*/
import { Injectable, inject } from '@angular/core';
import { EffectHandlerService } from '@services/effect-handler.service';
import { programmeProjectSelectionUnapplied } from '@state/account/account.actions';
import { ApiService } from '@state/api.service';
import { selectActiveResultsFilterId } from '@state/results-filters/results-filter.selectors';
import { concatMap } from 'rxjs';
import { filter, map, mergeMap, switchMap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { concatLatestFrom } from '@ngrx/operators';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ResultsFilterActions } from '@state/results-filters/results-filter.actions';
import { AvailableResultsFilter, ResultsFilter } from '@state/results-filters/results-filter.interfaces';


@Injectable()
export class ResultsFilterEffects {
  private actions$ = inject(Actions);
  private handler = inject(EffectHandlerService);
  private store = inject(Store);
  private api = inject(ApiService);


  /**
   * Create a new saved filter in the database, calls {@link createSuccess$} on
   * a success, retries if failed due to 401 error code and calls {@link createFailure()}
   * on failure otherwise.
   */
  create$ = createEffect(() => this.actions$.pipe(
    ofType(ResultsFilterActions.create),
    mergeMap(({ resultsFilter }) => this.api.createResultsFilter(resultsFilter).pipe(
      this.handler.effectResponse({
        success: (createdResultsFilter: ResultsFilter) => ResultsFilterActions.createSuccess(createdResultsFilter),
        retry: ResultsFilterActions.create(resultsFilter),
        failure: ResultsFilterActions.createFailure(),
        successMessage: `Filter ${resultsFilter.name} created successfully`,
        failureMessage: 'An unexpected error has occurred when saving the filter',
        showErrorDetail: true
      })
    ))
  ));

  /**
   * On successful creation of a ResultsFilter
   * calls {@link ResultsFilterActions.applyFilter} to set the created
   * filter as the active filter.
   */
  createSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(ResultsFilterActions.createSuccess),
    map(({ createdResultsFilter }) =>
      ResultsFilterActions.applyFilter(createdResultsFilter)
    ),
  ));


  /**
   * Retrieves a list of all the ResultsFilters available on this account, storing
   * this list in the store to display to the User.
   */
  loadAvailable$ = createEffect(() => this.actions$.pipe(
    ofType(ResultsFilterActions.loadAvailable),
    switchMap(() => this.api.listResultsFilters().pipe(
      this.handler.effectResponse({
        success: (availableFilters: AvailableResultsFilter[]) => ResultsFilterActions.loadAvailableSuccess({ availableFilters }),
        retry: ResultsFilterActions.loadAvailable(),
        failure: ResultsFilterActions.loadAvailableFailure(),
        failureMessage: 'An unexpected error has occurred loading the available save filters',
        showErrorDetail: true
      })
    ))
  ));

  /**
   * Triggers the load of the full data for a single ResultsFilter, on success
   * calls {@link ResultsFilterActions.applyFilter} to set the loaded filter as
   * the active filter
   */
  loadOne$ = createEffect(() => this.actions$.pipe(
    ofType(ResultsFilterActions.loadOne),
    mergeMap(({ uuid, name }) => this.api.retrieveResultsFilter(uuid).pipe(
      this.handler.effectResponse({
        success: (resultsFilter: ResultsFilter) => ResultsFilterActions.loadOneSuccess(resultsFilter),
        retry: ResultsFilterActions.loadOne({ uuid, name }),
        failure: ResultsFilterActions.loadOneFailure(),
        failureMessage: `An unexpected error has occurred when loading the ${name} filter.`,
        showErrorDetail: true,
      })
    ))
  ));

  /**
   * On successful load, sets the loaded filter as the active filter, doing it
   * in this way ensures the active filter is not set until after the filters
   * data has been loaded.
   */
  loadOneSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(ResultsFilterActions.loadOneSuccess),
    map(({ resultsFilter }) => ResultsFilterActions.applyFilter(resultsFilter))
  ));

  /**
   * Update a single results filter, replacing or adding the updated data to the
   * store.
   */
  updateOne$ = createEffect(() => this.actions$.pipe(
    ofType(ResultsFilterActions.updateOne),
    mergeMap(({ resultsFilter }) => this.api.updateResultsFilter(resultsFilter).pipe(
      this.handler.effectResponse({
        success: (updatedResultsFilter: ResultsFilter) => ResultsFilterActions.updateOneSuccess(updatedResultsFilter),
        retry: ResultsFilterActions.updateOne(resultsFilter),
        failure: ResultsFilterActions.updateOneFailure(),
        successMessage: `Saved search ${resultsFilter.name}, was successfully updated`,
        failureMessage: `An unexpected error has occurred when updating the ${resultsFilter.name} filter.`,
        showErrorDetail: true,
      })
    )))
  );

  /**
   * Trigger the deletion of a Results Filter and all the nested Parameter
   * Filters.
   */
  deleteOne$ = createEffect(() => this.actions$.pipe(
    ofType(ResultsFilterActions.deleteOne),
    mergeMap(({ uuid, name }: AvailableResultsFilter) => this.api.deleteResultsFilter(uuid).pipe(
      this.handler.effectResponse({
        success: () => ResultsFilterActions.deleteOneSuccess({ uuid, name }),
        retry: ResultsFilterActions.deleteOne({ uuid, name }),
        failure: ResultsFilterActions.deleteOneFailure(),
        successMessage: `Saved filter ${name}was  successfully deleted`,
        failureMessage: 'Error Deleting Saved Filter',
        showErrorDetail: true,
      })
    ))
  ));

  /**
   * When a Results Filter is deleted successfully, IF the deleted filter is
   * also the current selection, then clear the currently selected Filter.
   */
  deleteOneSuccessDeselect$ = createEffect(() => this.actions$.pipe(
    ofType(ResultsFilterActions.deleteOneSuccess),
    concatLatestFrom(() => this.store.select(selectActiveResultsFilterId)),
    filter(([deletedFilter, activeFilterID]) => deletedFilter.uuid === activeFilterID),
    map(() => ResultsFilterActions.clearFilter())
  ));

  /**
   * Trigger the partial update of a Result Filter to only update the name, uses
   * a separate 'rename' action so that there is no unintended patching of data
   */
  renameOne$ = createEffect(() => this.actions$.pipe(
    ofType(ResultsFilterActions.renameOne),
    concatMap(({ resultsFilter, name }) =>
      this.api.renameResultsFilter(resultsFilter.uuid, name).pipe(
        this.handler.effectResponse({
          success: (renamedResultsFilter: ResultsFilter) => ResultsFilterActions.renameOneSuccess(renamedResultsFilter),
          retry: ResultsFilterActions.renameOne({ resultsFilter, name }),
          failure: ResultsFilterActions.renameOneFailure(),
          successMessage: `Saved Filter was successfully renamed to ${name}`,
          failureMessage: `Error Renaming Saved Filter`,
          showErrorDetail: true,
        })
    )),
  ));

  /**
   * Clear the results filter store when the User changes account
   */
  whenAccountChanged$ = createEffect(() => this.actions$.pipe(
    ofType(programmeProjectSelectionUnapplied),
    map(() => ResultsFilterActions.clear())
  ));
}
